import {l} from "types/typescriptdefinisjoner_lederplan";
import {IDeferred, IPromise, IQService, IScope} from "angular";
import {BrukerRessurs} from "./ressurser/BrukerRessurs";
import {AutentiseringsRessurs} from "./ressurser/AutentiseringsRessurs";
import _ from "lodash";
import {regNavn} from "./tredjepartsTjenestenavn";
import moment from "moment";
import {Debugger} from 'debug'
import {UiRouterState} from "../infrastruktur/UiRouterFasade";
import {InterWindowBus} from "./InterWindowBus";
import {LokallagretAutentisertModel} from "felles/tjenester/LokallagretAutentisertModel";

const debug: Debugger = require('debug')('Kontekst');

/**
 * Brukers kontekst.
 *
 * Se lederplan-tjenester/src/main/java/no/systemfabrikken/lederplan/infrastruktur/sikkerhet/håndtering_av_jwt.md
 * for informasjon om requestflyten ifbm jwt håndtering
 */
export class Kontekst {
    static regNavn = "kontekst";

    /** event som trigges nå kontekst har blitt relastet */
    static brukerRelastetEvent = "kontekst:brukerRelastet";

    static forInjisering() {
        return [
            AutentiseringsRessurs.regNavn,
            BrukerRessurs.regNavn,
            InterWindowBus.regNavn,
            LokallagretAutentisertModel.regNavn,
            regNavn.ng.$rootScope,
            regNavn.ng.$q,
            Kontekst
        ]
    }

    constructor(private autentiseringsRes: AutentiseringsRessurs,
                private brukerRes: BrukerRessurs,
                private interWindowBus: InterWindowBus,
                private lokallagretAutentisertModel: LokallagretAutentisertModel,
                private $rootScope: IScope,
                private $q: IQService) {
        this._erLastetFraServer = $q.defer();
    }

    private _autentisert: l.AutentisertOkModell = null;
    private _autentisertBruker: l.BrukerModell = null;
    private _tilgjengeligeGrupper: l.TilgjengeligGruppeModell[] = null;
    private _grupperSomKreverAnnenAutentiseringsmetode: l.GruppeSomKreverAnnenAutentiseringsmetodeModell[] = null;
    private _invitasjonerTilGodkjenning: l.GruppeInvitasjonTilGodkjenningModell[] = null;
    private _kontaktpersonForAvtaler: l.AvtaleModell[] = null;
    private _brukervalg: l.Brukervalg = null;
    private _ikkePersistenteBrukervalg: IkkePersistenteBrukervalg = {};
    private _featurevalg: l.Featurevalg = null;
    private _aktivtTokenInfo: l.JwtTokenInfoModell = null;
    private _erLastetFraServer: IDeferred<void>;

    /** Oppdaterer Kontekst med informasjon fra angitt AutentisertOkModel. */
    populerKontekst(autentisert: l.AutentisertOkModell) {
        this.initFraAutentisert(autentisert);
        this._erLastetFraServer.resolve();
    }

    erLastetFraServer() {
        return this._erLastetFraServer.promise;
    }

    relastBruker(): IPromise<l.BrukerModell> {
        if (this._autentisertBruker === null) return this.$q.reject("bruker ikke autentisert");

        debug('relastBruker: relaster bruker');
        return this.brukerRes.hent(this._autentisertBruker.id).$promise
          .then(bruker => {
              this._autentisertBruker = bruker;
              this._autentisert.bruker = bruker;
              this.broadcastBrukerRelastetEventEvent();
              return bruker;
          });
    }

    /** Relaster innlogget bruker ved å hente kontekst på nytt fra server og oppdatere den lokale med returnerte data.
     * Fyrer {@link #brukerRelastetEvent} lokalt.
     * Fyrer {@link #brukerRelastetEvent} over InterWindowBus dersom interwindowbusBroadcastEvent=true. */
    relastBrukerOgGrupper(interwindowbusBroadcastEvent = true): IPromise<Kontekst> {
        if (this._autentisertBruker === null) return this.$q.reject("bruker ikke autentisert");

        debug('relastBrukerOgGrupper: relaster bruker og grupper');
        return this.autentiseringsRes.hentKontekst().$promise
          .then(autentisert => {
              this.initFraAutentisert(autentisert);
              this.broadcastBrukerRelastetEventEvent(interwindowbusBroadcastEvent);
              return <Kontekst>this;
          });
    }

    oppdaterAutentisertBruker(oppdatertBruker: l.BrukerModell) {
        //kalles ifbm endring av navn i "godta invitasjon og opprett bruker" og ved endring av meldingsinnstillinger
        //ingen krise at disse endringene ikke propagerer til ng2 konteksten
        _.assign(this._autentisertBruker, oppdatertBruker);
    }

    /** Fjerne all tidligere satt innloggingsinformasjon fra kontekst.
     * @see Kontekst#initFraAutentisert */
    nullstill() {
        debug('nullstill: nullstiller kontekst');
        this._autentisert = null;
        this._autentisertBruker = null;
        this._tilgjengeligeGrupper = null;
        this._grupperSomKreverAnnenAutentiseringsmetode = null;
        this._invitasjonerTilGodkjenning = null;
        this._kontaktpersonForAvtaler = null;
        this._brukervalg = null;
        this._featurevalg = null;
        this._aktivtTokenInfo = null;
    }

    autentisert(): l.AutentisertOkModell {
        return this._autentisert;
    }

    autentisertBruker(): l.BrukerModell {
        return this._autentisertBruker;
    }

    /** returnerer autentisert bruker id, null dersom ingen bruker innlogget.*/
    autentisertBrukersId(): string {
        return this._autentisertBruker?.id;
    }

    /** Innlogget brukers medlemskap i tilgjengelige grupper */
    medlemRepresentasjoner(): l.GruppeMedlemListeModell[] {
        return _(this._tilgjengeligeGrupper)
          .map(g => g.medlemskap)
          .value();
    }

    /** Innlogget brukers medlemskap i angitt gruppe, null dersom ikke funnet */
    medlemRepresentasjonTilknyttet(gruppeId: string): l.GruppeMedlemListeModell {
        return _(this._tilgjengeligeGrupper)
          .find(g => g.gruppe.id === gruppeId)
          ?.medlemskap
    }

    aktivtTokenTidTilUtløper(): moment.Duration {
        debug("aktivtTokenTidTilUtløper")
        return moment.duration(this.aktivtTokenInfo().tidTilUtloeper);
    }

    aktivtTokenInfo(): l.JwtTokenInfoModell {
        debug("aktivtTokenInfo")
        return this._aktivtTokenInfo;
    }

    /** grupper innlogget bruker har et aktivt medlemskap i og er autentisert med metode som aksepteres av gruppen */
    autentiserteGrupper(): l.EnkelGruppeModell[] {
        return _(this._tilgjengeligeGrupper).map(g => g.gruppe).value();
    }

    /** finn gruppe basert på id som innlogget bruker har et aktivt medlemskap i og er autentisert med metode som aksepteres av gruppen*/
    finnAutentiserteGruppe(gruppeId: string): l.EnkelGruppeModell {
        return _(this._tilgjengeligeGrupper).map(g => g.gruppe).find(g => g.id === gruppeId);
    }

    /** @deprecated denne baserer seg på at vi har oppdatert oversikt over alle tilgjengelige gruppers medlemmer - noe som ikke nødvendigvis er sant, og bør derfor ikke brukes.
     * man burde heller hente ferske data fra server.. */
    autentiserteGruppersMedlemmer(): l.GruppeMedlemListeModell[] {
        return <l.GruppeMedlemListeModell[]>_(this._tilgjengeligeGrupper)
          .map(gruppe => gruppe.aktiveMedlemmer)
          .flattenDeep()
          .value();
    }

    grupperSomKreverAnnenAutentiseringsmetode(): l.GruppeSomKreverAnnenAutentiseringsmetodeModell[] {
        return this._grupperSomKreverAnnenAutentiseringsmetode;
    }

    invitasjonerTilGodkjenning(): l.GruppeInvitasjonTilGodkjenningModell[] {
        return this._invitasjonerTilGodkjenning;
    }

    avtalerBrukerErKontaktpersonFor(): l.AvtaleModell[] {
        return this._kontaktpersonForAvtaler;
    }

    ikkePersistenteBrukervalg(): IkkePersistenteBrukervalg {
        return this._ikkePersistenteBrukervalg;
    }

    oppdaterIkkePersistenteBrukervalg(brukervalg: any) {
        _.assign(this._ikkePersistenteBrukervalg, _.cloneDeep(brukervalg));
    }

    brukervalg(): l.Brukervalg {
        return this._brukervalg;
    }

    oppdaterBrukervalg(brukervalg: Partial<l.Brukervalg>): IPromise<void> {
        if (this.erIkkeAutentisert()) return this.$q.resolve();

        var kopi = _.assign(_.cloneDeep(this._brukervalg), brukervalg);
        if (_.isEqual(kopi, this._brukervalg)) {
            debug("oppdaterBrukervalg like, dropper persistering", kopi, this._brukervalg)
            return this.$q.resolve();
        }

        _.assign(this._brukervalg, _.cloneDeep(brukervalg));
        this.lokallagretAutentisertModel.oppdaterBrukervalg(this._brukervalg);
        return this.brukerRes.persisterBrukervalg(this._brukervalg).$promise;
    }

    featurevalg(): l.Featurevalg {
        return this._featurevalg;
    }

    erFeatureAktivert(featurenavn: string): boolean {
        return this.featurevalg()[featurenavn] === true;
    }

    erAutentisert(): boolean {
        return this._autentisertBruker != null;
    }

    erIkkeAutentisert(): boolean {
        return !this.erAutentisert();
    }

    /**
     * Returnerer usann dersom
     * - bruker ikke er innlogget og state.data.tilgangsroller ikke inneholder l.TilgansrollerBruker.anonym
     * - innlogget bruker bruker ikke har noen av rollen angitt i state.data.tilgangsroller
     */
    harTilgangTilState(state: UiRouterState): boolean {
        var rollerSomGirAdgang: l.TilgansrollerBruker[] = state.tilgangsroller;

        if (!_.isArray(rollerSomGirAdgang)) return false;
        if (_.includes(rollerSomGirAdgang, l.TilgansrollerBruker.anonym)) return true;

        if (!this.erAutentisert()) return false;
        if (_.intersection(this._autentisertBruker.tilgangsroller, rollerSomGirAdgang).length > 0) return true;

        return false;
    }

    /** sann dersom innlogget bruker har angitt tilgansrollerBruker */
    inneharTilgangsrolle(rolle: l.TilgansrollerBruker): boolean {
        if (!this.erAutentisert()) return false;
        return _(this._autentisertBruker.tilgangsroller).includes(rolle);
    }

    /** sann dersom innlogget brukers medlemrepresentasjon i angitt gruppe har angitt tilgansrollerMedlem */
    inneharTilgangsrolleIGruppe(rolle: l.TilgangsrolleMedlem, gruppeId: string): boolean {
        if (!this.erAutentisert()) return false;
        if (_.isNil(gruppeId)) return false;
        const medlem = this.medlemRepresentasjonTilknyttet(gruppeId);
        if (_.isNil(medlem)) return false;
        return _.includes(medlem.tilgangsroller, rolle);
    }

    /** sann dersom minst en av innlogget brukers medlemrepresentasjon har angitt tilgansrollerMedlem */
    inneharTilgangsrolleINoenGruppe(rolle: l.TilgangsrolleMedlem): boolean {
        if (!this.erAutentisert()) return false;
        return _(this.medlemRepresentasjoner()).some(m => _.includes(m.tilgangsroller, rolle));
    }

    /** sann dersom innlogget bruker har et aktivt medlemskap i angitt gruppe og er autentisert med metode som aksepteres av gruppen */
    harTilgangTilGruppe(gruppeId: string) {
        return _(this.autentiserteGrupper()).some(autGruppe => autGruppe.id === gruppeId);
    }

    /** Sørger for å relaste bruker dersom bruker ble relastet i anne arkfane */
    lyttTilBrukerRelastetIAnnenFane(){
        this.interWindowBus.ved(Kontekst.brukerRelastetEvent, (bruker:l.BrukerModell) => {
            debug('lyttTilBrukerRelastetIAnnenFane', bruker);
            this.relastBrukerOgGrupper(false);
        });
    }

    //hjelpemetoder

    /** Initialiserer kontekst fra angitt autentisert.
     * @see Kontekst#nullstill() */
    private initFraAutentisert(autentisert: l.AutentisertOkModell) {
        debug("initFraAutentisert - populerer kontekst fra %O", autentisert)
        this._autentisert = autentisert;
        this._autentisertBruker = autentisert.bruker;
        this._brukervalg = this.initBrukervalg(autentisert.brukervalg);
        this._featurevalg = this.initFeaturevalg(autentisert.featurevalg);
        this._tilgjengeligeGrupper = autentisert.tilgjengeligeGrupper;
        this._grupperSomKreverAnnenAutentiseringsmetode = autentisert.grupperSomKreverAnnenAutentiseringsmetode;
        this._invitasjonerTilGodkjenning = autentisert.invitasjonerTilGodkjenning;
        this._kontaktpersonForAvtaler = autentisert.kontaktpersonForAvtaler;
        this._aktivtTokenInfo = autentisert.jwtTokenInfo;
    }

    private initBrukervalg(brukervalg: l.Brukervalg): l.Brukervalg {
        return {...Kontekst.standardBrukervalg(), ...brukervalg};
    }

    static standardBrukervalg(): l.Brukervalg {
        return {
            dashboardfane: undefined,
            dashboardfilter: {
                gruppeIder: [],
                periode: undefined,
                gruppering: l.SaksgrupperingPlan.tema,
                visningsvalg: 'saksnummer',
                visSaksdetaljer: true,
                visAarshjulinnslag: true
            },
            dashboardEkspanderteGrupper: [],
            introduksjoner: {
                avspilte: {}
            }
        }
    }

    private initFeaturevalg(featurevalg: l.Featurevalg): l.Featurevalg {
        return {...Kontekst.standardFeaturevalg(), ...featurevalg};
    }

    static standardFeaturevalg(): l.Featurevalg {
        return {
        }
    }

    /** Broadcaster lokalt og til andre faner at innlogget bruker er relastet. */
    private broadcastBrukerRelastetEventEvent(interwindowbusBroadcastLogutEvent = true) {
        debug('trigger brukerRelastetEvent event');
        this.$rootScope.$broadcast(Kontekst.brukerRelastetEvent, this.autentisertBruker());
        if (interwindowbusBroadcastLogutEvent) this.interWindowBus.signaliser(Kontekst.brukerRelastetEvent, this.autentisertBruker());
    }

}

export interface IkkePersistenteBrukervalg {
    oppgaveFilter?: any;
    oppgaveVisning?: string;
    beslutningFilter?: any;
}

